(ns app.front.view.project
  (:require [app.domain.municipality.model :as municipality-model]
            [app.domain.project.model :as project-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.search-result :as sr]
            [app.front.task :as task]
            [app.front.ui :as ui]
            [app.front.ui.messages-panel :as messages-panel]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.ui.typography :refer [h3]]
            [app.front.ui.updated :as ui-updated]
            [app.front.util :as futil]
            [app.front.view.create-application.create-v2 :as create-v2]
            [app.front.view.fee.summary :as fee-summary]
            [app.front.view.inspections.summary-subsection :as inspections-summary-subsection]
            [app.front.view.project.bills :as project-bills]
            [app.front.view.project.conditions :as project-conditions]
            [app.front.view.project.deficiencies :as project-deficiencies]
            [app.front.view.workspace.get :as ws-get]
            [app.front.view.workspace.header :as ws-header]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))


(rf/reg-event-db ::reset
  (fn [db]
    (update db
            ::project-page merge {:loading?             true
                                  ::name-in-editor      nil
                                  ::address-selectable? false
                                  :workspaces           nil})))

(defn project-ev-handler [action success-tr]
  (fn [_ [_ project-id]]
    {:comm/command {:command    action
                    :data       {:id project-id}
                    :on-success [::entities/apply-changes]
                    :notify     {:success success-tr}}}))

(rf/reg-event-fx ::close-project
  (project-ev-handler :project/close :project/closed))

(rf/reg-event-fx ::reopen-project
  (project-ev-handler :project/reopen :project/reopened))

(rc/reg-chain ::load-project
  (fn [_ [_ project-id]]
    {:comm/command {:command :project/get
                    :data    {:project/id project-id}}})

  (fn [_ [_ _ status response]]
    {:dispatch [::entities/merge-entity-db status response]}))

(defn in-execution-state? [ws-result]
  (-> ws-result :workspace-state (= :workspace.state/execution)))


(rf/reg-event-fx ::load-project-workspaces
  (fn [{:keys [db]} [_ project-id sort-condition]]
    (let [sort-condition (or sort-condition
                             (get-in db [::project-page ::search-config :sort-condition]))]
      {:comm/command {:command    :workspace/find
                      :data       (p/assoc-if
                                    {:conditions [{:filter.condition/attribute :project-id
                                                   :filter.condition/operator  :equals
                                                   :filter.condition/values    #{project-id}}]}
                                    :sort-condition sort-condition)
                      :on-success [::load-project-workspaces.success]}})))

(rf/reg-event-fx ::load-project-workspaces.success
  (fn [{:keys [db]} [_ _ {:keys [results]}]]
    {:db (-> db
             (update ::project-page assoc :workspaces results)
             (update ::project-page assoc :loading? false))}))


(rf/reg-event-fx ::load-search-config
  (fn [{:keys [db]} _]
    {:comm/command {:command          :user/search-config
                    :on-success       [::load-search-config.success]
                    :finally-dispatch [::load-search-config.done]}
     :db           (update-in db [::project-page ::search-config] assoc :loading? true)}))


(rf/reg-event-db ::load-search-config.success
  (fn [db [_ _ {:keys [sort-options sort-condition]}]]
    (-> db
        (update-in [::project-page ::search-config] assoc :sort-options sort-options)
        (update-in [::project-page ::search-config] update :sort-condition (fnil identity sort-condition)))))


(rf/reg-event-db ::load-search-config.done
  #(assoc-in % [::project-page ::search-config :loading?] false))


(rf/reg-event-db ::set-sort-condition
  (fn [db [_ sort-condition]]
    (assoc-in db [::project-page ::search-config :sort-condition] sort-condition)))


(rf/reg-event-db ::set-name-in-editor
  (fn [db [_ name]]
    (assoc-in db [::project-page ::name-in-editor] name)))


(rf/reg-event-db ::set-address-selectable
  (fn [db [_ selectable?]]
    (assoc-in db [::project-page ::address-selectable?] selectable?)))


(rf/reg-event-fx ::save-changes
  (fn [_ [_ changes]]
    {:comm/command {:command    :project/update
                    :data       changes
                    :on-success [::save-changes.success]}}))


(rf/reg-event-fx ::save-changes.success
  (fn [{:keys [db]} [_ status body]]
    {:dispatch-n [[::entities/merge-entity-db status body]
                  [:notifications/add :toast :success :success :changes-saved]]
     :db         (update db ::project-page merge {::name-in-editor      nil
                                                  ::address-selectable? false})}))

(rf/reg-sub ::project-page :-> ::project-page)

(rf/reg-sub ::project-workspaces
  :<- [::project-page]
  :-> :workspaces)

(rf/reg-sub ::execution-workspaces
  :<- [::project-workspaces]
  (fn [results _]
    (->> results
         (filter in-execution-state?))))

(rf/reg-sub ::has-execution-workspaces?
  :<- [::execution-workspaces]
  (fn [wses _]
    (-> wses seq boolean)))

(rf/reg-sub ::project-addresses
  :<- [::project-workspaces]
  (fn [workspaces _]
    (->> workspaces
         (filter :workspace/address)
         (map #(select-keys % [:workspace/address :workspace/id]))
         (group-by :workspace/address)
         (map #(->> %
                    second
                    (sort-by :workspace/id)
                    first))
         (sort-by :workspace/address))))


(rf/reg-sub ::property-tr
  :<- [:tr]
  :<- [::project-workspaces]
  :<- [::entities/context]
  (fn [[tr workspaces ctx] _]
    (->> (entities/find-entity ctx {:group/id (some :group/id workspaces)})
         :entity/parent
         (property-model/property-tr-fn tr))))

(rf/reg-sub ::sort-condition
  :<- [::search-config]
  :-> :sort-condition)


(rf/reg-sub ::name-in-editor
  :<- [::project-page]
  :-> ::name-in-editor)


(rf/reg-sub ::address-selectable?
  :<- [::project-page]
  :-> ::address-selectable?)


(rf/reg-sub ::search-config
  :<- [::project-page]
  :-> ::search-config)

(rf/reg-sub ::loading?
  :<- [::project-page]
  :<- [::search-config]
  (fn [[view-state search-config] _]
    (or (:loading? view-state) (:loading? search-config))))


(rf/reg-sub ::back-href
  (fn [_]
    (href/href :dashboard)))


(defn get-project-id [match]
  (-> match :parameters :path :project-id))


(rf/reg-sub ::view-project-entity
  :<- [:route/match]
  :<- [::entities/context]
  (fn [[match ctx] _]
    (let [project-id (get-project-id match)]
      (entities/find-entity ctx {:db/id project-id}))))


(rf/reg-sub ::options
  :<- [:session/user]
  :<- [::view-project-entity]
  :<- [::property-tr]
  (fn [[user project-entity tr] _]
    (let [project-id (:db/id project-entity)]
      {:items (cond-> []
                (project-model/can-reopen? user project-entity)
                (conj {:id       "reopen-project"
                       :label    (tr [:project/reopen-project])
                       :on-click #(>evt [::reopen-project project-id])})

                (project-model/can-close? user project-entity)
                (conj {:id       "close-project"
                       :label    (tr [:project/close-project])
                       :on-click #(>evt [::close-project project-id])}))})))


(rf/reg-sub ::view-data
  :<- [::back-href]
  :<- [::options]
  :<- [::primary-action]
  (fn [[back-href options primary-action]]
    (p/assoc-if {:back-href back-href
                 :options   options}
                :primary-action primary-action)))


(defn project-editable?
  [{:keys [granted-permissions :domain/state]}]
  (and (contains? (set granted-permissions) :role.project/writer)
       (= state :project.state/active)))


(rf/reg-sub ::primary-action
  :<- [::view-project-entity]
  :<- [::create-v2/start-create?]
  (fn [[project-entity creating?] _]
    (let [{:municipality/keys [number subdivision]} (:project/municipality project-entity)
          project-id                                (:db/id project-entity)]
      (when (project-editable? project-entity)
        (if creating?
          {:evt    [::create-v2/close-create]
           :tr-key :close}
          {:evt    [::create-v2/start-create number subdivision project-id]
           :tr-key :project/new-workspace})))))


(defn heading-label-value [tr label-k & content]
  [:div.bx--col-xs-12.bx--col-sm-6.bx--col-md-4
   [:div.mb-2.text-sm.text-gray-700 (tr [label-k])]
   (into
     [:div.pb-2.flex.items-center {:style {:min-height "48px"}}]
     content)])


(defn project-address [tr {:project/keys [structural-address] :as project-entity}]
  (let [available-addresses (<sub [::project-addresses])]
    (if (<sub [::address-selectable?])
      [heading-label-value
       tr
       :workspace/address
       [ui-select/select {:id           "project-address-select"
                          :class-name   "full-width-select"
                          :label-text   (tr [:project/select-address])
                          :hide-label   true
                          :required     false
                          :allow-empty? false
                          :values       available-addresses
                          :val->str     :workspace/address
                          :on-change    #(>evt [::save-changes {:id                   (:db/id project-entity)
                                                                :address-workspace-id (:workspace/id %)}])}]
       [:span.ml-2
        (cc/button {:kind             :ghost
                    :size             :small
                    :icon             i/remove
                    :icon-position    :right
                    :icon-description (tr [:cancel])
                    :class-name       "only-icon-button"
                    :on-click         #(>evt [::set-address-selectable false])}
                   "")]]
      [heading-label-value
       tr
       :workspace/address
       (:address/full-address structural-address (tr [:address/address-missing]))
       (when (and (project-editable? project-entity)
                  (du/at-least-two? available-addresses))
         (cc/button {:kind             :ghost
                     :size             :small
                     :icon             i/pen
                     :icon-position    :right
                     :icon-description (tr [:project/select-address])
                     :class-name       "ml-2 only-icon-button"
                     :on-click         #(>evt [::set-address-selectable true])}
                    ""))])))


(defn project-location [tr {:project/keys [property] :as project-entity}]
  [:div.bx--row
   [project-address tr project-entity]

   (when property
     [heading-label-value tr :property/id (:property/id property)])

   (when-let [zoning (:property/zoning property)]
     [heading-label-value tr :property/zoning zoning])])


(defn project-municipality [{:keys [municipality]}]
  (let [tr   (<sub [:tr])
        lang (<sub [:language])]
    [:div.bx--row.page-heading
     [:div.bx--col-xs-12
      [h3 (municipality-model/common-name-province municipality tr lang)]]]))

(defn closed-desc [tr project-entity]
  (when (du/in-state? :project.state/closed project-entity)
    (tr [:project/closed-by] [(-> project-entity
                                  :project/closed-by
                                  user-model/full-name)
                              ((<sub [::date-util/date-fn]) (:project/closed-ts project-entity))])))

(defn project-heading [tr loading? {:project/keys [municipality] :as project-entity}]
  (let [name-in-editor (<sub [::name-in-editor])]
    [:<>
     [:div.ml-8.flex.flex-wrap.items-end
      [:div.flex.items-end.flex-1.mr-2
       (cond
         loading?
         (cc/skeleton-text {:heading true})

         name-in-editor
         [:<>
          [ui/text-input {:id         "project-name-editor"
                          :value      name-in-editor
                          :label-text (tr [:project/name-label])
                          :on-change  #(rf/dispatch-sync [::set-name-in-editor (futil/tval %)])}]
          [:div
           (cc/button {:kind             :ghost
                       :icon             i/remove
                       :icon-position    :right
                       :icon-description (tr [:cancel])
                       :class-name       "only-icon-button"
                       :on-click         #(>evt [::set-name-in-editor nil])}
                      "")
           (cc/button {:kind             :ghost
                       :icon             i/check
                       :icon-position    :right
                       :icon-description (tr [:save])
                       :class-name       "only-icon-button"
                       :disabled         (str/blank? name-in-editor)
                       :on-click         #(>evt [::save-changes {:id           (:db/id project-entity)
                                                                 :project/name name-in-editor}])}
                      "")]]

         :else
         [:div.flex.items-baseline.justify-start
          [:h1.mt-6 (:project/name project-entity)]
          (when (project-editable? project-entity)
            (cc/button {:kind             :ghost
                        :icon             i/pen
                        :icon-position    :right
                        :icon-description (tr [:project/edit-name])
                        :class-name       "ml-2 only-icon-button"
                        :on-click         #(>evt [::set-name-in-editor (:project/name project-entity)])}
                       ""))])]]

     [:div.pl-8.block.lg:hidden ; show project closure date in header if narrow screen
      [closed-desc tr project-entity]]

     [project-municipality (util/mmap municipality)]

     [project-location tr project-entity]]))

(defn workspaces-heading-content [{:keys [tr project-entity]}]
  (let [{:keys [sort-options]} (<sub [::search-config])]
    [:span
     [sr/search-result-sort
      (util/mmap {:value     (<sub [::sort-condition])
                  :on-change (fn [v]
                               (>evt [::set-sort-condition v])
                               (>evt [::load-project-workspaces (:db/id project-entity) v]))}
                 sort-options tr)]]))

(defn workspace-id-col
  "Takes workspace results (NOT entities) from workspace/find and renders a column"
  [tr {:workspace/keys   [category-names permit-type permit-type-names municipal-case-id id]
       :application/keys [work-target-names]}]
  (let [lang              (<sub [:language])
        permit-type-label (or (i18n-model/get-text-from-entities-or-map permit-type-names lang)
                              (tr [permit-type]))]
    [:div.flex.flex-col.pr-4
     [h3 "font-bold mb-2"
      (->> [(or (i18n-model/get-text category-names lang)
                permit-type-label)
            (i18n-model/get-text work-target-names lang)]
           (filter some?)
           (str/join " - "))]
     [:span
      (futil/str-and-desc
        (when category-names
          ;; do not show permit-type twice when no category
          permit-type-label)
        (or municipal-case-id
            id))]]))

(defn state-and-update-col [tr result]
  [:div.flex.flex-col
   [:span {:class (str "uppercase mb-2 " (ws-util/workspace-emphasis (:phase-state result)))} (ws-util/workspace-state tr result)]
   [ui-updated/updated {:ts   (:modified result)
                        :user (:modified-by result)}]])

(def button-wrapper
  :div.bg-ui-dark-gray.p-2.mr-4.relative)

(defn conversation-link [workspace]
  [:a
   {:href     "#"
    :on-click #(do
                 (.preventDefault %)
                 (>evt [::messages-panel/toggle-open (:workspace/id workspace)])
                 (>evt [:route/push {:route  :view/workspace
                                     :params {:workspace-id (:workspace/id workspace)}}]))}
   [button-wrapper
    (cc/icon {:icon        i/conversation
              :description "conversations"})
    (when (:unseen-messages? workspace)
      [:div.cloudpermit--notification-badge
       {:style {:right "3px" :top "3px" :margin-top "0px" :height "10px" :min-width "10px"}}])]])


(defn email-notifications [workspace tr]
  (r/with-let [notifications-disabled? (r/atom (:notifications-disabled? workspace))]
    [button-wrapper
     {:class    "cursor-pointer"
      :on-click (fn [_]
                  (>evt [::ws-header/email-subscription workspace @notifications-disabled?])
                  (swap! notifications-disabled? not))}
     (cc/icon {:icon        (if @notifications-disabled? i/envelope-off i/envelope)
               :description (tr [(if @notifications-disabled? :search/enable-email :search/disable-email)])})]))


(defn actions-col [tr workspace]
  (let [authority-group-ids (->> (<sub [:session/authority-groups])
                                 (map :group/id)
                                 set)]
    [:div.flex.items-center
     (when (empty? authority-group-ids)
       [email-notifications workspace tr])
     (when (or (empty? authority-group-ids)
               (-> workspace :group/id authority-group-ids))
       [conversation-link workspace])]))

(defn workspaces-content [{:keys [tr]}]
  [ui-table/modern-responsive-table
   {:items       (<sub [::project-workspaces])
    :headerless? true
    :link        {:description ws-util/workspace-link-description
                  :make-href   ws-util/workspace-or-resume-application-href}
    :id-func     :workspace/id
    :cols        [[nil
                   (partial workspace-id-col tr)
                   {}]
                  [nil
                   :workspace/address
                   {:width "25%"}]
                  [nil
                   (partial state-and-update-col tr)
                   {:width "275px"}]
                  [nil
                   (partial actions-col tr)
                   {:width (if (<sub [:session/authority-user?]) "94px" "144px")
                    :class "self-center"}]]}])

(defn project-section [{:keys [loading? title heading-extra tr]} content]
  [section/section
   title
   (util/mmap heading-extra tr)
   title
   (if loading?
     [:div.bx--col-xs-12
      (cc/skeleton-text {:paragraph true :line-count 5})]
     content)])

(defn project-deficiencies-and-orders [{:keys [tr project-id]}]
  (let [show-orders? (<sub [::project-deficiencies/has-orders? project-id])]
    (if (or (<sub [::project-deficiencies/has-deficiencies? project-id]) show-orders?)
      [inspections-summary-subsection/deficiencies-and-orders
       (util/mmap {:orders-sub       [::project-deficiencies/project-orders project-id]
                   :deficiencies-sub [::project-deficiencies/project-deficiencies project-id]}
                  tr show-orders?)]
      [h3 (tr [:project/no-deficiencies])])))

(defn dashlet-id [project-id]
  (str "project-" project-id "-tasks"))

(defn project-page [match]
  (let [project-id     (get-project-id match)
        tr             (<sub [::property-tr])
        project-entity (<sub [::entities/entity project-id])
        loading?       (<sub [::loading?])]
    [:div.project-page.cloudpermit-page
     [create-v2/create-preliminary-selections tr true]
     [ui/page-header-el
      [project-heading tr loading? project-entity]]
     [task/task-dashlet {:id               (dashlet-id project-id)
                         :heading-i18n-key :project/tasks-heading}]
     [project-section (util/mmap {:title         (tr [:project/applications-title])
                                  :heading-extra [workspaces-heading-content (util/mmap tr project-entity)]}
                                 loading? tr)
      [workspaces-content (util/mmap tr)]]
     (when (or loading?
               (<sub [::ws-get/fetching-any-workspace?])
               (<sub [::project-conditions/has-conditions? project-id]))
       [project-section (util/mmap {:title (tr [:view/application-conditions])}
                                   tr loading?)
        [project-conditions/conditions-list (<sub [::project-conditions/conditions project-id])]])
     (when (or loading?
               (<sub [::ws-get/fetching-any-workspace?])
               (<sub [::project-bills/has-bills? project-id]))
       [project-section (util/mmap {:title (tr [:project/bills])}
                                   tr loading?)
        [fee-summary/bills-list (util/mmap {:bills-sub     [::project-bills/bills project-id]
                                            :show-address? true}
                                           tr)]])
     (when (<sub [::has-execution-workspaces?])
       [:<>
        [project-section (util/mmap {:title (tr [:project/deficiencies-and-orders])}
                                    tr loading?)
         [project-deficiencies-and-orders (util/mmap project-id tr)]]
        (when (or loading?
                  (<sub [::project-deficiencies/has-any-inspections? project-id]))
          [project-section (util/mmap {:title (tr [:project/inspections-title])}
                                      tr loading?)
           [inspections-summary-subsection/inspections
            (util/mmap {:workspaces-sub [::execution-workspaces]}
                       tr)]])])]))

(defn project-breadcrumb [{:keys [tr match]}]
  (let [{:keys [project-id]} (-> match :parameters :path)
        project              (when (integer? project-id)
                               (<sub [::entities/entity project-id]))]
    [:view/project {:project-id project-id} (or (:project/name project) (tr [:applications/project]))]))

(def project-view
  {:name        :view/project
   :view        project-page
   :view-sub    [::view-data]
   :breadcrumb  {:items [project-breadcrumb]}
   :parameters  (st/merge
                  {:path {:project-id s/Int}}
                  (st/optional-keys
                    {:subdivision  s/Str
                     :municipality s/Str}))
   :controllers [{:parameters {:path [:project-id]}
                  :start      (fn [params]
                                (let [project-id (-> params :path :project-id)]
                                  (>evt [::reset])
                                  (>evt [::load-project (-> params :path :project-id)])
                                  (>evt [::load-project-workspaces (-> params :path :project-id)])
                                  (>evt [::task/load-my-tasks (dashlet-id project-id) project-id])
                                  (>evt [::load-search-config])
                                  (>evt [::create-v2/close-create])))}]
   :public?     false})
